import type { DeviceOperatorSnapshot } from "@shared/identity";

export type LocalOperatorUnlock = {
  displayName: string;
  actorId: string;
  storeId: string;
  deviceId: string;
  salt: string;
  verifier: string;
  createdAt: string;
};

const ITERATIONS = 100_000;

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

async function derive(pin: string, salt: Uint8Array): Promise<string> {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: salt.buffer as ArrayBuffer, iterations: ITERATIONS, hash: "SHA-256" },
    key,
    256,
  );
  return toBase64(new Uint8Array(bits));
}

async function verifyServerVerifier(pin: string, encoded: string): Promise<boolean> {
  const [algorithm, iterationText, saltText, expectedText] = encoded.split("$");
  const iterations = Number(iterationText);
  if (algorithm !== "pbkdf2-sha256" || !Number.isSafeInteger(iterations) || !saltText || !expectedText) return false;
  try {
    const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, ["deriveBits"]);
    const bits = await crypto.subtle.deriveBits(
      { name: "PBKDF2", salt: fromBase64(saltText).buffer as ArrayBuffer, iterations, hash: "SHA-256" },
      key,
      256,
    );
    return toBase64(new Uint8Array(bits)) === expectedText;
  } catch {
    return false;
  }
}

export async function createLocalOperatorUnlock(input: { displayName: string; actorId: string; storeId: string; deviceId: string; pin: string }): Promise<LocalOperatorUnlock> {
  if (!/^\d{4,12}$/.test(input.pin)) throw new Error("PIN must contain 4 to 12 digits");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return {
    displayName: input.displayName,
    actorId: input.actorId,
    storeId: input.storeId,
    deviceId: input.deviceId,
    salt: toBase64(salt),
    verifier: await derive(input.pin, salt),
    createdAt: new Date().toISOString(),
  };
}

export async function verifyLocalOperatorPin(pin: string, record: LocalOperatorUnlock): Promise<boolean> {
  if (!/^\d{4,12}$/.test(pin)) return false;
  return (await derive(pin, fromBase64(record.salt))) === record.verifier;
}

export async function findDeviceOperatorByPin(pin: string, operators: DeviceOperatorSnapshot[]): Promise<DeviceOperatorSnapshot | null> {
  if (!/^\d{4,12}$/.test(pin)) return null;
  const matches: DeviceOperatorSnapshot[] = [];
  for (const operator of operators) {
    if (!operator.active || !operator.membershipActive || !(await verifyServerVerifier(pin, operator.pinVerifier))) continue;
    matches.push(operator);
  }
  return matches.length === 1 ? matches[0]! : null;
}

export function parseLocalOperatorUnlock(value: string | null): LocalOperatorUnlock | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<LocalOperatorUnlock>;
    if (!parsed.displayName || !parsed.actorId || !parsed.storeId || !parsed.deviceId || !parsed.salt || !parsed.verifier) return null;
    return parsed as LocalOperatorUnlock;
  } catch {
    return null;
  }
}
